import React, { useState, useEffect } from "react";
import {
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Box,
  CircularProgress,
  IconButton,
  Tooltip,
  TablePagination,
  Menu,
  TextField,
  Button,
  Stack,
} from "@mui/material";
import axios from "axios";
import { makeStyles } from "@mui/styles";
import {
  PictureAsPdf,
  InsertDriveFile,
  TableChart,
  FilterAlt,
} from "@mui/icons-material";
import jsPDF from "jspdf";
import "jspdf-autotable";
import * as XLSX from "xlsx";
import { toast } from "react-toastify";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider, MobileDatePicker } from "@mui/x-date-pickers";
import dayjs from "dayjs";

const useStyles = makeStyles(() => ({
  evenRow: {
    backgroundColor: "#FFFFFF",
  },
  oddRow: {
    backgroundColor: "#D0EFFF",
  },
}));

const ManuallyPayoutTxn = () => {
  const classes = useStyles();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [downloadLoading, setDownloadLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [totalPages, setTotalPages] = useState(0);
  const [totalElements, setTotalElements] = useState(0);
  const [filters, setFilters] = useState({
    startDate: null,
    endDate: null,
    searchBy: "",
  });
  const [filterMenuAnchor, setFilterMenuAnchor] = useState(null);

  const fetchData = async (page = 0, size = 10, currentFilters = filters) => {
    try {
      setLoading(true);
      const params = {
        page,
        size,
        fromDate: currentFilters.startDate
          ? dayjs(currentFilters.startDate).format("YYYY-MM-DD")
          : "",
        toDate: currentFilters.endDate
          ? dayjs(currentFilters.endDate).format("YYYY-MM-DD")
          : "",
        searchBy: currentFilters.searchBy,
      };
      const response = await axios.get(
        "https://api.ipaisa.co.in/myapp/auth/getAllModifiedTransactions",
        {
          headers: {
            Authorization: `Bearer ${sessionStorage.getItem("jwtToken")}`,
          },
          params,
        }
      );

      if (response.status === 204) {
        setData([]);
        setTotalPages(0);
        setTotalElements(0);
      } else if (response.data) {
        setData(response.data.data || []);
        setTotalPages(response.data.totalPages);
        setTotalElements(response.data.totalElements);
      } else {
        setData([]);
        setTotalPages(0);
        setTotalElements(0);
      }
    } catch (error) {
      console.error("Error fetching data:", error);
      toast.error("Failed to fetch data");
      setData([]);
      setTotalPages(0);
      setTotalElements(0);
    } finally {
      setLoading(false);
    }
  };

  const fetchAllData = async () => {
    setDownloadLoading(true);
    try {
      const params = {
        fromDate: filters.startDate
          ? dayjs(filters.startDate).format("YYYY-MM-DD")
          : "",
        toDate: filters.endDate
          ? dayjs(filters.endDate).format("YYYY-MM-DD")
          : "",
        searchBy: filters.searchBy,
        page: 0,
        size: totalElements > 0 ? totalElements : 1000,
      };

      const response = await axios.get(
        "https://api.ipaisa.co.in/myapp/auth/getAllModifiedTransactions",
        {
          headers: {
            Authorization: `Bearer ${sessionStorage.getItem("jwtToken")}`,
          },
          params,
        }
      );

      if (response.status === 204) {
        return [];
      }
      return response.data.data || [];
    } catch (error) {
      if (error.response && error.response.status === 204) {
        return [];
      }
      console.error("Error fetching all data:", error);
      toast.error("Failed to fetch data for download");
      return [];
    } finally {
      setDownloadLoading(false);
    }
  };

  useEffect(() => {
    fetchData(currentPage, pageSize, filters);
  }, [currentPage, pageSize]);

  const handleChangePage = (event, newPage) => {
    setCurrentPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setPageSize(parseInt(event.target.value, 10));
    setCurrentPage(0);
  };

  const handleFilterMenuClick = (event) => {
    setFilterMenuAnchor(event.currentTarget);
  };

  const handleFilterMenuClose = () => {
    setFilterMenuAnchor(null);
  };

  const handleDateChange = (key, newValue) => {
    setFilters((prev) => ({
      ...prev,
      [key]: newValue,
    }));
  };

  const handleSearchChange = (event) => {
    setFilters((prev) => ({
      ...prev,
      searchBy: event.target.value,
    }));
  };

  const applyFilters = () => {
    setCurrentPage(0);
    fetchData(0, pageSize, filters);
    handleFilterMenuClose();
  };

  const clearFilters = () => {
    const clearedFilters = {
      startDate: null,
      endDate: null,
      searchBy: "",
    };
    setFilters(clearedFilters);
    setCurrentPage(0);
    fetchData(0, pageSize, clearedFilters);
    handleFilterMenuClose();
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleString();
  };

  const formatAmount = (amount) => {
    if (amount === null || amount === undefined) return "N/A";
    return Number(amount).toFixed(2);
  };

  const handleDownloadPDF = async () => {
    const allData = await fetchAllData();

    if (!allData || allData.length === 0) {
      toast.info("No data available to download.");
      return;
    }
    try {
      setDownloadLoading(true);

      const doc = new jsPDF("landscape");

      // Add title
      doc.setFontSize(16);
      doc.text("Modified Payout Transactions Report", 14, 15);

      const columns = [
        "Sr. No.",
        "Date & Time",
        "Transaction ID",
        "External Ref",
        "Status",
        "Account No.",
        "Account Name",
        "Amount",
        "Charges",
        "Mode",
        "Bank",
        "Modified By",
        "Last Updated",
      ];

      const rows = allData.map((item, index) => [
        index + 1,
        formatDate(item.payout.timestamp),
        item.payout.txnReferenceId || "N/A",
        item.payout.externalRef || "N/A",
        item.payout.status || "N/A",
        item.payout.payeeAccount || "N/A",
        item.payout.payeeName || "N/A",
        formatAmount(item.payout.txnValue),
        formatAmount(item.payout.txnCharges),
        item.payout.transfer_mode || "N/A",
        item.payout.bankName || "N/A",
        item.handleBy?.lastHandledByUser ||
          item.handleBy?.handleByUser ||
          "N/A",
        item.handleBy?.lastUpdated
          ? formatDate(item.handleBy.lastUpdated)
          : "N/A",
      ]);

      doc.autoTable({
        head: [columns],
        body: rows,
        startY: 25,
        theme: "grid",
        styles: {
          fontSize: 8,
          cellPadding: 2,
        },
        headStyles: {
          fillColor: [2, 123, 254],
          textColor: [255, 255, 255],
          fontSize: 9,
          fontStyle: "bold",
        },
        alternateRowStyles: {
          fillColor: [240, 248, 255],
        },
      });

      const currentDateTime = new Date().toISOString().split("T")[0];
      doc.save(`modified_payout_transactions_${currentDateTime}.pdf`);
    } catch (error) {
      console.error("Error downloading PDF:", error);
      toast.error("Failed to download PDF");
    } finally {
      setDownloadLoading(false);
    }
  };

  const handleDownloadExcel = async () => {
    const allData = await fetchAllData();
    if (!allData || allData.length === 0) {
      toast.info("No data available to download.");
      return;
    }
    try {
      setDownloadLoading(true);

      const excelData = allData.map((item, index) => ({
        "Sr. No.": index + 1,
        "Date & Time": formatDate(item.payout.timestamp),
        "Transaction ID": item.payout.txnReferenceId || "N/A",
        "External Ref": item.payout.externalRef || "N/A",
        Status: item.payout.status || "N/A",
        "Account No.": item.payout.payeeAccount || "N/A",
        "Account Name": item.payout.payeeName || "N/A",
        Amount: formatAmount(item.payout.txnValue),
        Charges: formatAmount(item.payout.txnCharges),
        Mode: item.payout.transfer_mode || "N/A",
        Bank: item.payout.bankName || "N/A",
        "Modified By":
          item.handleBy?.lastHandledByUser ||
          item.handleBy?.handleByUser ||
          "N/A",
        "Last Updated": item.handleBy?.lastUpdated
          ? formatDate(item.handleBy.lastUpdated)
          : "N/A",
        "Opening Balance": formatAmount(item.payout.walletOpeningBalance),
        "Closing Balance": formatAmount(item.payout.walletClosingBalance),
        Remark: item.payout.remark || "N/A",
      }));

      const worksheet = XLSX.utils.json_to_sheet(excelData);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(
        workbook,
        worksheet,
        "Modified Transactions"
      );

      // Set column widths
      const columnWidths = [
        { wch: 8 }, // Sr. No.
        { wch: 20 }, // Date & Time
        { wch: 15 }, // Transaction ID
        { wch: 15 }, // External Ref
        { wch: 20 }, // Status
        { wch: 15 }, // Account No.
        { wch: 25 }, // Account Name
        { wch: 12 }, // Amount
        { wch: 12 }, // Charges
        { wch: 10 }, // Mode
        { wch: 20 }, // Bank
        { wch: 15 }, // Modified By
        { wch: 20 }, // Last Updated
        { wch: 15 }, // Opening Balance
        { wch: 15 }, // Closing Balance
        { wch: 30 }, // Remark
      ];
      worksheet["!cols"] = columnWidths;

      const currentDateTime = new Date().toISOString().split("T")[0];
      XLSX.writeFile(
        workbook,
        `modified_payout_transactions_${currentDateTime}.xlsx`
      );
    } catch (error) {
      console.error("Error downloading Excel:", error);
      toast.error("Failed to download Excel");
    } finally {
      setDownloadLoading(false);
    }
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Box style={{ padding: "2rem", marginTop: "calc(3% + 56px)" }}>
        <Grid container spacing={2}>
          {/* Title and Download Buttons */}
          <Grid
            item
            xs={12}
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Typography variant="h6" sx={{ fontWeight: "bold" }}>
              Modified Payout Transactions
            </Typography>
            <Grid sx={{ display: "flex", gap: 2, alignItems: "center" }}>
              <Tooltip title="Filter" arrow>
                <IconButton onClick={handleFilterMenuClick}>
                  <FilterAlt />
                </IconButton>
              </Tooltip>
              <Menu
                anchorEl={filterMenuAnchor}
                open={Boolean(filterMenuAnchor)}
                onClose={handleFilterMenuClose}
                PaperProps={{
                  sx: {
                    p: 2,
                    width: "300px",
                  },
                }}
              >
                <Stack spacing={2}>
                  <Typography variant="subtitle1">Filters</Typography>
                  <MobileDatePicker
                    label="Start Date"
                    value={filters.startDate}
                    onChange={(newValue) =>
                      handleDateChange("startDate", newValue)
                    }
                  />
                  <MobileDatePicker
                    label="End Date"
                    value={filters.endDate}
                    onChange={(newValue) =>
                      handleDateChange("endDate", newValue)
                    }
                  />
                  <TextField
                    label="Search"
                    variant="outlined"
                    value={filters.searchBy}
                    onChange={handleSearchChange}
                    fullWidth
                  />
                  <Stack direction="row" spacing={1} justifyContent="flex-end">
                    <Button onClick={clearFilters} color="secondary">
                      Clear
                    </Button>
                    <Button onClick={applyFilters} variant="contained">
                      Apply
                    </Button>
                  </Stack>
                </Stack>
              </Menu>
              <Tooltip title="Download as PDF" arrow>
                <IconButton
                  onClick={handleDownloadPDF}
                  disabled={downloadLoading}
                  sx={{
                    color: "red",
                    backgroundColor: "rgba(255, 0, 0, 0.1)",
                    border: "2px solid rgba(255, 0, 0, 0.3)",
                    borderRadius: "15px",
                    transition: "0.3s",
                    "&:hover": {
                      backgroundColor: "red",
                      color: "white",
                    },
                  }}
                >
                  {downloadLoading ? (
                    <CircularProgress size={24} color="error" />
                  ) : (
                    <PictureAsPdf />
                  )}
                </IconButton>
              </Tooltip>
              <Tooltip title="Download as Excel" arrow>
                <IconButton
                  onClick={handleDownloadExcel}
                  disabled={downloadLoading}
                  sx={{
                    color: "green",
                    backgroundColor: "rgba(0, 128, 0, 0.1)",
                    border: "2px solid rgba(0, 128, 0, 0.3)",
                    borderRadius: "15px",
                    transition: "0.3s",
                    "&:hover": {
                      backgroundColor: "green",
                      color: "white",
                    },
                  }}
                >
                  {downloadLoading ? (
                    <CircularProgress size={24} color="success" />
                  ) : (
                    <TableChart />
                  )}
                </IconButton>
              </Tooltip>
            </Grid>
          </Grid>

          {/* Table */}
          <Grid item xs={12}>
            <TableContainer
              component={Paper}
              sx={{ borderRadius: 2, boxShadow: "none" }}
            >
              <Table>
                <TableHead sx={{ bgcolor: "#027BFE" }}>
                  <TableRow>
                    <TableCell sx={{ color: "white" }}>Sr. No.</TableCell>
                    <TableCell sx={{ color: "white" }}>Date & Time</TableCell>
                    <TableCell sx={{ color: "white" }}>UTR</TableCell>
                    <TableCell sx={{ color: "white" }}>External Ref</TableCell>
                    <TableCell sx={{ color: "white" }}>Account No.</TableCell>
                    <TableCell sx={{ color: "white" }}>Account Name</TableCell>
                    <TableCell sx={{ color: "white" }}>Amount</TableCell>
                    <TableCell sx={{ color: "white" }}>Charges</TableCell>
                    <TableCell sx={{ color: "white" }}>Mode</TableCell>
                    <TableCell sx={{ color: "white" }}>Bank</TableCell>
                    <TableCell sx={{ color: "white" }}>Modified By</TableCell>
                    <TableCell sx={{ color: "white" }}>Last Updated</TableCell>
                    <TableCell sx={{ color: "white" }}>Status</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loading ? (
                    <TableRow>
                      <TableCell colSpan={13} align="center" sx={{ py: 3 }}>
                        <CircularProgress />
                      </TableCell>
                    </TableRow>
                  ) : data.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={13} align="center">
                        No records found
                      </TableCell>
                    </TableRow>
                  ) : (
                    data.map((item, index) => (
                      <TableRow
                        key={index}
                        className={
                          index % 2 === 0 ? classes.evenRow : classes.oddRow
                        }
                      >
                        <TableCell>
                          {currentPage * pageSize + index + 1}
                        </TableCell>
                        <TableCell>
                          {formatDate(item.payout.timestamp)}
                        </TableCell>
                        <TableCell>
                          {item.payout.txnReferenceId || "N/A"}
                        </TableCell>
                        <TableCell>
                          {item.payout.externalRef || "N/A"}
                        </TableCell>
                        <TableCell>
                          {item.payout.payeeAccount || "N/A"}
                        </TableCell>
                        <TableCell>{item.payout.payeeName || "N/A"}</TableCell>
                        <TableCell>
                          {formatAmount(item.payout.txnValue)}
                        </TableCell>
                        <TableCell>
                          {formatAmount(item.payout.txnCharges)}
                        </TableCell>
                        <TableCell>
                          {item.payout.transfer_mode || "N/A"}
                        </TableCell>
                        <TableCell>{item.payout.bankName || "N/A"}</TableCell>
                        <TableCell>
                          {item.handleBy?.lastHandledByUser ||
                            item.handleBy?.handleByUser ||
                            "N/A"}
                        </TableCell>
                        <TableCell>
                          {item.handleBy?.lastUpdated
                            ? formatDate(item.handleBy.lastUpdated)
                            : "N/A"}
                        </TableCell>
                        <TableCell>{item.payout.status || "N/A"}</TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Grid>

          {/* Pagination */}
          {!loading && data.length > 0 && (
            <Grid item xs={12}>
              <TablePagination
                rowsPerPageOptions={[10, 25, 50, 100]}
                component="div"
                count={totalElements}
                rowsPerPage={pageSize}
                page={currentPage}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
              />
            </Grid>
          )}
        </Grid>
      </Box>
    </LocalizationProvider>
  );
};

export default ManuallyPayoutTxn;
